import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import type { UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  MultiSelect,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@klareospace/pharos-lib-web-utils';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';

import type { UseDriverVehicleFieldReturn } from '../../drivers/components/driver-vehicle-field';
import { DriverVehicleField } from '../../drivers/components/driver-vehicle-field';
import type { CarrierSiteFilter } from '../../carriers/utils';
import { getExistingSiteScopeOptions } from '../hooks/use-driver-fields';
import type { UserFormInput } from '../schemas/user-form.schema';
import { ScopeFields } from './scope-fields';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UserFormFieldsProps {
  carrierOptions: MultiSelectOption[];
  driverVehicleField: UseDriverVehicleFieldReturn;
  form: UseFormReturn<UserFormInput>;
  handleSiteSelectionChange: (spaceId: string, siteIds: string[]) => void;
  /** true once the form's selected roles include the driver role — same definition in create and edit. */
  isDriverRole: boolean;
  isSiteLocked?: boolean;
  isSpaceLocked?: boolean;
  lockedSiteId?: string | null;
  onSendPasswordReset?: () => void;
  roleOptions: MultiSelectOption[];
  /** When true (drivers entry point, edit mode), the roles field is fully disabled — role
   * management for such users happens exclusively from the users entry point. */
  rolesDisabled?: boolean;
  /** Roles that stay selected but cannot be removed (drivers entry point, create mode) — forwarded
   * to the roles MultiSelect's `lockedValues`. */
  rolesLockedValues?: string[];
  selectedSpaceIds: string[];
  siteFilterForSpace?: (spaceId: string) => CarrierSiteFilter;
  spaceLabelMap: Map<string, string>;
  spaceOptions: MultiSelectOption[];
  /**
   * The user being edited (with resolved scopes for site labels), or null when creating.
   * isEditMode is derived as user !== null. resolvedScopes is used to build existingSiteOptions
   * so drifted site scopes remain visible with labels.
   */
  user: UserWithResolvedScopes | null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface DriverFieldsProps {
  carrierLabel: string;
  carrierOptions: MultiSelectOption[];
  carrierPlaceholder: string;
  driverVehicleField: UseDriverVehicleFieldReturn;
  form: UseFormReturn<UserFormInput>;
}

function DriverFields({
  carrierLabel,
  carrierOptions,
  carrierPlaceholder,
  driverVehicleField,
  form,
}: DriverFieldsProps): React.JSX.Element {
  return (
    <>
      <FormField
        control={form.control}
        name="carrierId"
        render={({ field }): React.JSX.Element => (
          <FormItem>
            <FormLabel>
              {carrierLabel} <span>*</span>
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={carrierPlaceholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {carrierOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <DriverVehicleField
        control={form.control}
        filteredVehicleOptions={driverVehicleField.filteredVehicleOptions}
        setVehiclePopoverOpen={driverVehicleField.setVehiclePopoverOpen}
        setVehicleSearch={driverVehicleField.setVehicleSearch}
        vehicleOptions={driverVehicleField.vehicleOptions}
        vehiclePopoverOpen={driverVehicleField.vehiclePopoverOpen}
        vehicleSearch={driverVehicleField.vehicleSearch}
      />
    </>
  );
}

/**
 * Both modes' carrier label resolve via the shared `tCommon`, under different keys. Edit mode's
 * placeholder is passed in already-resolved so this helper never calls `t()` against `settings`.
 */
function resolveDriverCarrierLabels(
  isEditMode: boolean,
  tCommon: (key: string) => string,
  editCarrierPlaceholder: string,
): { carrierLabel: string; carrierPlaceholder: string } {
  return {
    carrierLabel: isEditMode ? tCommon('fields.carrier') : tCommon('fields.carrierRoleLabel'),
    carrierPlaceholder: isEditMode ? editCarrierPlaceholder : tCommon('fields.carrierPlaceholder'),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function UserFormFields({
  carrierOptions,
  driverVehicleField,
  form,
  handleSiteSelectionChange,
  isDriverRole,
  isSiteLocked = false,
  isSpaceLocked = false,
  lockedSiteId,
  onSendPasswordReset,
  roleOptions,
  rolesDisabled = false,
  rolesLockedValues,
  selectedSpaceIds,
  siteFilterForSpace,
  spaceLabelMap,
  spaceOptions,
  user,
}: UserFormFieldsProps): React.JSX.Element {
  const { i18n, t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');

  const isEditMode = user !== null;

  // i18n prefix differs between modes
  const prefix = isEditMode ? 'users.edit.fields' : 'users.inviteDrawer';
  const { carrierLabel, carrierPlaceholder } = resolveDriverCarrierLabels(
    isEditMode,
    tCommon,
    t('users.edit.fields.carrierPlaceholder'),
  );

  const existingSiteOptions = getExistingSiteScopeOptions(user);

  return (
    <Form {...form}>
      <form className="space-y-4">
        {/* ── Name fields ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {tCommon('fields.firstName')} <span>*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} {...(isEditMode ? { placeholder: t(`${prefix}.firstNamePlaceholder`) } : {})} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {tCommon('fields.lastName')} <span>*</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} {...(isEditMode ? { placeholder: t(`${prefix}.lastNamePlaceholder`) } : {})} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* ── Email ───────────────────────────────────────────────────────── */}
        {isEditMode ? (
          <div className="space-y-2">
            <label
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              htmlFor="edit-user-email"
            >
              {tCommon('auth.email')} <span className="text-destructive">*</span>
            </label>
            <Input disabled id="edit-user-email" value={user?.email ?? ''} />
            <button
              className="mt-1 flex cursor-pointer items-center gap-1 text-sm text-primary hover:underline"
              onClick={onSendPasswordReset}
              type="button"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {t('users.edit.sendPasswordReset')}
            </button>
          </div>
        ) : (
          <FormField
            control={form.control}
            name="email"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {tCommon('auth.email')} <span>*</span>
                </FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* ── Phone ───────────────────────────────────────────────────────── */}
        <FormField
          control={form.control}
          name="phone"
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{tCommon('fields.phone')}</FormLabel>
              <FormControl>
                <PhoneInput
                  countryAriaLabel={tCommon('phoneInput.countryAriaLabel')}
                  emptyText={tCommon('phoneInput.noCountryFound')}
                  locale={i18n.language}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  searchPlaceholder={tCommon('phoneInput.searchPlaceholder')}
                  value={field.value ?? null}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* ── Roles ───────────────────────────────────────────────────────── */}
        <FormField
          control={form.control}
          name="roles"
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>
                {tCommon('fields.role')}
                {!isEditMode && <span> *</span>}
              </FormLabel>
              <FormControl>
                <MultiSelect
                  disabled={rolesDisabled}
                  lockedValues={rolesLockedValues}
                  onValueChange={field.onChange}
                  options={roleOptions}
                  placeholder={t('common.rolePlaceholder')}
                  searchable={false}
                  value={field.value ?? []}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* ── Scope fields ────────────────────────────────────────────────── */}
        <ScopeFields
          existingSiteOptions={existingSiteOptions}
          form={form}
          handleSiteSelectionChange={handleSiteSelectionChange}
          isSiteLocked={isSiteLocked}
          isSpaceLocked={isSpaceLocked}
          lockedSiteId={lockedSiteId}
          selectedSpaceIds={selectedSpaceIds}
          siteFilterForSpace={siteFilterForSpace}
          spaceLabelMap={spaceLabelMap}
          spaceOptions={spaceOptions}
        />

        {/* ── Driver fields ───────────────────────────────────────────────── */}
        {isDriverRole && (
          <DriverFields
            carrierLabel={carrierLabel}
            carrierOptions={carrierOptions}
            carrierPlaceholder={carrierPlaceholder}
            driverVehicleField={driverVehicleField}
            form={form}
          />
        )}
      </form>
    </Form>
  );
}
